import React, { useEffect, useState, useContext } from 'react';
import Legend from './Legend';
import { digitCommaFormat, addDays, monthShort, weekdaysShort } from '../AirtimePlanningUtils';
import { disabledStyle } from '../AirtimePlanningStyles';
import { SystemSettingsContext } from '../../../../contexts/SystemSettingsContext';
import { withOutTimeZoneDate } from '../../../../services/DateService';
import moment from 'moment';
const scaleRowHeight = 41;
const maxShowDays = 14;
const maxShowWeeks = 27;
const numberOfScaleRows = 8;
const viewBoxHeight = scaleRowHeight * numberOfScaleRows * 2;
const viewBoxWidth = 3160;

const rowStyle = {
    boxSizing: 'border-box',
    height: scaleRowHeight,
    border: '1px solid #D7D7D7',
    backgroundColor: '#FFFFFF',
    marginLeft: 123,
    marginRight: 115,
    display: 'flex',
    justifyContent: 'space-between'
};

const zeroScaleStyle = {
    ...rowStyle,
    height: 23,
    border: '1px solid #FFFFFF'
};

const dataMargins = {
    display: 'flex',
    justifyContent: 'space-around',
    marginLeft: 130,
    marginRight: 130
};

const leftScale = {
    marginLeft: -45
};

const rightScale = {
    marginRight: -84
};

const labelStyle = {
    color: '#414141',
    fontFamily: 'Roboto',
    fontSize: 14,
    fontWeight: 'bold'
};

const dateLabelStyle = {
    color: '#414141',
    fontFamily: 'Roboto',
    fontSize: 12,
    fontWeight: 500
};

const dayLabels = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 72
};

const PlanChart = ({
    hasPlan,
    planSummary,
    quoteExpired,
    isPlanning,
    showImpacts,
    setShowImpacts,
    showBaseImpacts,
    setShowBaseImpacts,
    planExpired
}) => {
    const { formatCurrency } = useContext(SystemSettingsContext);

    const [dateSpots, setDateSpots] = useState([]);
    const [dateImpacts, setDateImpacts] = useState([]);
    const [startDate, setStartDate] = useState(null);
    const [startMonday, setStartMonday] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [maxSpots, setMaxSpots] = useState(0);
    const [maxImpactsOrCosts, setMaxImpactsOrCosts] = useState(0);
    const [showDays, setShowDays] = useState(false);
    const [showWeeks, setShowWeeks] = useState(false);
    const [maxCols, setMaxCols] = useState(0);
    const [columnIndexes, setColumnIndexes] = useState([]);
    const [impactLabel, setImpactLabel] = useState(<></>);
    const [spotsLabel, setSpotsLabel] = useState(<></>);

    const getMonthIndex = (dtDate) => {
        const dtStr = window.translate(monthShort(dtDate.getMonth())) + ' ' + dtDate.getFullYear();
        return dtStr;
    };

    const colIndexByDate = (dtDate, dtFirstMonday, isByDay, isByWeek, spotcount) => {
        const spotDate = addDays(dtDate, 0);
        if (isByDay) return dtDate;
        else if (isByWeek) {
            // 3600 for daylight saving
            const dt1 = moment(spotDate);
            const dt2 = moment(dtFirstMonday);

            // const daysBetween = Math.round(dt1 - dt2) / (1000 * 60 * 60 * 24) + 1;
            const daysBetween = dt1.diff(dt2, 'days') + 1;
            const wk = Math.ceil(daysBetween / 7);
            return Math.ceil(daysBetween / 7);
        } else {
            return getMonthIndex(spotDate);
        }
    };

    const calculateTableData = () => {
        const dtSpots = [];
        const dtImpactsOrCosts = [];
        const colIndexes = [];
        let mSpots = 0;
        let mImpactsOrCosts = 0;
        const sDate = addDays(planSummary[0]?.orderedDate, 0);
        const eDate = addDays(planSummary[planSummary.length - 1]?.orderedDate, 0);
        setStartDate(sDate);
        const firstMonday = new Date(sDate);
        while (firstMonday.getDay() !== 1) {
            firstMonday.setDate(firstMonday.getDate() - 1);
        }
        setStartMonday(firstMonday);
        setEndDate(eDate);
        const daysBetween = Math.floor((eDate - sDate) / (1000 * 60 * 60 * 24)) + 1;
        const isByDay = daysBetween <= maxShowDays;
        setShowDays(isByDay);
        const isByWeek = daysBetween > maxShowDays && daysBetween / 7 < maxShowWeeks;
        setShowWeeks(isByWeek);

        for (let i = 0; i < planSummary?.length; i++) {
            const colIndex = colIndexByDate(
                planSummary[i]?.orderedDate,
                firstMonday,
                isByDay,
                isByWeek,
                planSummary[i]?.spotCount
            );
            if (!colIndexes.includes(colIndex)) colIndexes.push(colIndex);
            dtSpots[colIndex] = (dtSpots[colIndex] ?? 0) + planSummary[i]?.spotCount;
            mSpots = mSpots < dtSpots[colIndex] ? dtSpots[colIndex] : mSpots;
            dtImpactsOrCosts[colIndex] =
                (dtImpactsOrCosts[colIndex] ?? 0) +
                (showBaseImpacts
                    ? planSummary[i]?.baseImpacts
                    : showImpacts
                    ? planSummary[i]?.targetImpacts
                    : planSummary[i]?.price);

            mImpactsOrCosts =
                mImpactsOrCosts < dtImpactsOrCosts[colIndex] ? dtImpactsOrCosts[colIndex] : mImpactsOrCosts;
        }

        setColumnIndexes(colIndexes);
        mSpots = Math.ceil(mSpots / 8) * 8;
        const impactDivisor = 8;
        //     mImpacts < 100 ? 10 : mImpacts < 1000 ? 100 : 1000;
        mImpactsOrCosts = Math.ceil(mImpactsOrCosts / impactDivisor) * impactDivisor;
        setDateSpots(dtSpots);
        setDateImpacts(dtImpactsOrCosts);
        setMaxSpots(mSpots);
        setMaxImpactsOrCosts(mImpactsOrCosts);

        setMaxCols(
            isByDay
                ? Math.min(Object.keys(dtSpots)?.length, maxShowDays)
                : isByWeek
                ? Math.min(Object.keys(dtSpots)?.length, maxShowWeeks)
                : Math.min(Object.keys(dtSpots)?.length, 24)
        );
    };

    useEffect(() => {
        if (planSummary && Object.keys(planSummary).length > 0) {
            calculateTableData();
        }
    }, [planSummary, showImpacts, showBaseImpacts]);

    const scaleValues = () => {
        const spotsScale = (scaleNum) => {
            const mSpots = Math.ceil(maxSpots / numberOfScaleRows);
            return mSpots > 0 ? (mSpots * scaleNum).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '';
        };

        const impactsScale = (scaleNum) => {
            const mImpacts = Math.ceil(maxImpactsOrCosts / numberOfScaleRows);
            return showBaseImpacts || showImpacts
                ? mImpacts > 0
                    ? digitCommaFormat(mImpacts * scaleNum)
                    : ''
                : formatCurrency(mImpacts * scaleNum)
                      .toString()
                      .replace(/(\.|,)00$/g, '');
        };

        let scaleLabels;
        for (let i = numberOfScaleRows; i >= 0; i--) {
            const currentScaleLabels =
                hasPlan && !isPlanning ? (
                    <div key={i} style={i === 0 ? zeroScaleStyle : rowStyle}>
                        <span style={{ ...labelStyle, ...leftScale }}>{spotsScale(i)}</span>
                        <span
                            style={{
                                ...labelStyle,
                                ...rightScale,
                                ...(planExpired ? {} : { color: '#FD9A00' })
                            }}
                        >
                            {impactsScale(i)}
                        </span>
                    </div>
                ) : (
                    <div key={i} style={i === 0 ? zeroScaleStyle : rowStyle}>
                        <div
                            style={{
                                ...disabledStyle,
                                width: 70,
                                height: 16,
                                marginLeft: -81
                            }}
                        />
                        <div
                            style={{
                                ...disabledStyle,
                                width: 80,
                                height: 16,
                                marginRight: -91
                            }}
                        />
                    </div>
                );
            scaleLabels = [scaleLabels, currentScaleLabels];
        }
        return scaleLabels;
    };

    const displayTextWidth = (text, font) => {
        const canvas = displayTextWidth.canvas || (displayTextWidth.canvas = document.createElement('canvas'));
        const context = canvas.getContext('2d');
        context.font = font;
        const metrics = context.measureText(text);
        return metrics.width;
    };

    const makeValueLabel = (event, value) => {
        const rHeight = 35;
        const valueStr = showBaseImpacts || showImpacts ? digitCommaFormat(value) : formatCurrency(value);
        const rWidth = displayTextWidth(valueStr, '28px Roboto') + 35;
        const rx = event?.target?.cx?.baseVal?.valueAsString - rWidth / 2;
        const ryCheck = event?.target?.cy?.baseVal?.value - 80;
        // if value is too high move hover label below
        const ry = ryCheck < 0 ? event?.target?.cy?.baseVal?.value + 45 : ryCheck;
        const ryText = ryCheck < 0 ? ry - 8 + rHeight : ry + 10 + rHeight / 2;

        const txtWidth = displayTextWidth(valueStr) / 2;

        setImpactLabel(
            <g>
                <rect
                    x={rx.toString()}
                    y={ry.toString()}
                    width={rWidth.toString()}
                    height={rHeight.toString()}
                    fill='#FFFFFF'
                    stroke='#D7D7D7'
                    strokeWidth='1'
                />
                <text
                    x={rx - txtWidth + rWidth / 2}
                    y={ryText}
                    fontFamily='Roboto'
                    fontSize={28}
                    fontWeight='bold'
                    fill={quoteExpired || planExpired ? '#CCCCCC' : '#FD9A00'}
                    dominantBaseline='center'
                    textAnchor='center'
                >
                    {valueStr}
                </text>
            </g>
        );
    };

    const makeSpotsLabel = (event, value) => {
        const rHeight = 35;
        const rWidth = 108;
        const rx = (event?.target?.x?.baseVal?.value + 2).toString();
        const txtWidth = displayTextWidth(digitCommaFormat(value), '28px Roboto') / 2;

        setSpotsLabel(
            <g>
                <rect
                    x={rx.toString()}
                    y={610}
                    width={rWidth}
                    height={rHeight}
                    fill='#FFFFFF'
                    stroke='#D7D7D7'
                    // fill='#FDF1D6'
                    // stroke='#FDF1D6'
                    strokeWidth='1'
                />
                <text
                    x={rx - txtWidth + rWidth / 2}
                    y={637}
                    fontFamily='Roboto'
                    fontSize={28}
                    fontWeight='bold'
                    fill={quoteExpired ? '#CCCCCC' : '#414141'}
                    dominantBaseline='center'
                    textAnchor='center'
                >
                    {digitCommaFormat(value)}
                </text>
            </g>
        );
    };

    const spotsBarsAndImpactsLines = () => {
        let path = '';
        let circles;
        let allBars;
        if (columnIndexes?.length > 0) {
            const distanceBetweenPoints = viewBoxWidth / columnIndexes?.length;
            let x = distanceBetweenPoints / 2;
            const mImpactsOrCosts = Math.ceil(maxImpactsOrCosts / numberOfScaleRows);

            let maxBarHeight = 0;
            const mSpots = Math.ceil(maxSpots / numberOfScaleRows);
            if (mSpots > 0 || mImpactsOrCosts > 0) {
                for (let i = 0; i < columnIndexes?.length; i++) {
                    const colIndex = columnIndexes[i];
                    const impacts = dateImpacts[colIndex];
                    const y = viewBoxHeight - (impacts / mImpactsOrCosts) * (scaleRowHeight * 2);
                    if (i > 0) x += distanceBetweenPoints;
                    path +=
                        i === 0
                            ? 'M' + x.toString() + ' ' + y.toString() + ' '
                            : 'L' + x.toString() + ' ' + y.toString() + ' ';
                    const spots = (
                        <circle
                            fill='#FFFFFF'
                            stroke={planExpired || quoteExpired || isPlanning ? '#CCCCCC' : '#FD9A00'}
                            strokeWidth={6}
                            cx={x.toString()}
                            cy={y.toString()}
                            r='8'
                            onMouseOver={(e) => makeValueLabel(e, impacts.toString())}
                            onMouseOut={() => setImpactLabel(<></>)}
                        />
                    );
                    circles = [circles, spots];

                    const daysSpots = dateSpots[colIndex];
                    let barHeight = Math.floor(daysSpots / mSpots) * scaleRowHeight;
                    barHeight = (daysSpots % mSpots) * (scaleRowHeight / mSpots) + barHeight;
                    maxBarHeight = barHeight > maxBarHeight ? barHeight : maxBarHeight;
                    barHeight = barHeight * 2;
                    const currentBar = (
                        <g>
                            <rect
                                x={(x - 56).toString()}
                                y={viewBoxHeight - barHeight}
                                width={112}
                                height={barHeight.toString()}
                                fill={planExpired || quoteExpired || isPlanning ? '#CCCCCC' : '#FDF1D6'}
                                stroke={planExpired || quoteExpired || isPlanning ? '#A6A6A6' : '#FFB50C'}
                                strokeWidth='1'
                                onMouseOver={(e) => makeSpotsLabel(e, daysSpots.toString())}
                                onMouseOut={() => setSpotsLabel(<></>)}
                            />
                        </g>
                    );
                    allBars = [allBars, currentBar];
                }
                maxBarHeight = Math.floor(maxBarHeight + 25) * -1;
            }
        }

        return (
            <div
                style={{
                    ...dataMargins,
                    marginTop: -372,
                    alignItems: 'baseline',
                    paddingBottom: 40
                }}
            >
                <svg
                    id='impactlines'
                    data-name='impact lines'
                    xmlns='http://www.w3.org/2000/svg'
                    viewBox={'0 0 ' + viewBoxWidth.toString() + ' ' + viewBoxHeight.toString()}
                >
                    {allBars}
                    <path
                        fill='none'
                        stroke={planExpired || quoteExpired || isPlanning ? '#CCCCCC' : '#FD9A00'}
                        strokeWidth={6}
                        d={path}
                    />
                    {circles}
                    {impactLabel}
                    {spotsLabel}
                </svg>
            </div>
        );
    };

    const getDayLabel = (dtDate, dayOffSet) => {
        if (dtDate) {
            const dtDateCopy = addDays(dtDate, dayOffSet);
            const dayNum = dtDateCopy.getDay();
            const dayString = weekdaysShort(dayNum);
            return window.translate(dayString);
        }
        return '';
    };

    const getDateLabel = (dtDate, dayOffSet, showFullYear = false) => {
        if (dtDate) {
            const dtDateCopy = addDays(dtDate, dayOffSet);
            const dtStr =
                window.translate(monthShort(dtDateCopy.getMonth())) +
                ' ' +
                dtDateCopy.getDate() +
                ' ' +
                (showFullYear ? dtDateCopy.getFullYear() : dtDateCopy.getFullYear() % 100);
            return dtStr;
        }
        return '';
    };

    const getWeekCommencingLabel = (dtDate, weekOffset) => {
        if (dtDate) {
            const dtDateCopy = addDays(dtDate, weekOffset * 7);
            const dtStr = 'wc ' + window.translate(monthShort(dtDateCopy.getMonth())) + ' ' + dtDateCopy.getDate();
            return dtStr;
        }
        return '';
    };

    const getMonthLabel = (dtDate, monthOffset) => {
        if (dtDate) {
            const dtDateCopy = new Date(dtDate);
            dtDateCopy.setMonth(dtDateCopy.getMonth() + monthOffset);
            const dtStr = getMonthIndex(dtDateCopy);
            return dtStr;
        }
        return '';
    };

    const bottomLabels = () => {
        let bottomLabels;
        if (showDays) {
            // max 14 cols
            for (let i = 0; i < maxCols; i++) {
                const currentLabel = (
                    <div style={dayLabels}>
                        <span style={labelStyle}>{getDayLabel(startDate, i)}</span>
                        <span style={dateLabelStyle}>{getDateLabel(startDate, i)}</span>
                    </div>
                );
                bottomLabels = [bottomLabels, currentLabel];
            }
        } else if (showWeeks) {
            // max 26 cols
            for (let i = 0; i < maxCols; i++) {
                const currentLabel = (
                    <div style={dayLabels}>
                        <span style={labelStyle}>Wk.{('0' + (i + 1)).slice(-2)}</span>
                        <span style={dateLabelStyle}>{getWeekCommencingLabel(startMonday, i)}</span>
                    </div>
                );
                bottomLabels = [bottomLabels, currentLabel];
            }
        } else {
            // max 24 cols
            for (let i = 0; i < maxCols; i++) {
                const currentLabel = (
                    <div style={{ ...dayLabels, width: 98 }}>
                        <span style={labelStyle}>{getMonthLabel(startDate, i)}</span>
                        <span style={dateLabelStyle}>
                            {i === 0
                                ? 'mc ' + getDateLabel(startDate, i, true)
                                : i === maxCols - 1
                                ? 'me ' + getDateLabel(endDate, i, true)
                                : ''}
                        </span>
                    </div>
                );
                bottomLabels = [bottomLabels, currentLabel];
            }
        }
        return bottomLabels;
    };

    return (
        <>
            <Legend
                hasPlan={hasPlan}
                quoteExpired={quoteExpired || planExpired}
                isPlanning={isPlanning}
                showImpacts={showImpacts}
                setShowImpacts={setShowImpacts}
                showBaseImpacts={showBaseImpacts}
                setShowBaseImpacts={setShowBaseImpacts}
            />
            <div style={{ marginTop: hasPlan && !isPlanning ? 25.5 : 15 }}>
                {scaleValues()}
                <div
                    style={{
                        ...rowStyle,
                        height: 1,
                        border: '1px solid #FFFFFF',
                        ...(!hasPlan || isPlanning ? { marginLeft: 137 } : {})
                    }}
                >
                    {hasPlan && !isPlanning ? (
                        <>
                            <span
                                style={{
                                    ...labelStyle,
                                    ...leftScale,
                                    marginLeft: -82
                                }}
                            >
                                {window.translate('Start Date')}
                            </span>
                            <span
                                style={{
                                    ...labelStyle,
                                    ...rightScale,
                                    marginRight: -68
                                }}
                            >
                                {window.translate('End Date')}
                            </span>
                        </>
                    ) : (
                        <>
                            <div
                                style={{
                                    ...disabledStyle,
                                    width: 70,
                                    height: 35,
                                    ...leftScale,
                                    marginLeft: -95
                                }}
                            />
                            <div
                                style={{
                                    ...disabledStyle,
                                    width: 80,
                                    height: 35,
                                    ...rightScale,
                                    marginRight: -91
                                }}
                            />
                        </>
                    )}
                </div>

                <div
                    style={{
                        ...dataMargins,
                        marginTop: -11,
                        ...(!hasPlan || isPlanning ? { marginLeft: 123, marginRight: 115 } : {})
                    }}
                >
                    {hasPlan && !isPlanning ? (
                        bottomLabels()
                    ) : (
                        <div
                            style={{
                                ...disabledStyle,
                                width: '100%',
                                height: 35
                            }}
                        />
                    )}
                </div>
                {spotsBarsAndImpactsLines()}
            </div>
        </>
    );
};

export default PlanChart;
